import React, { useEffect, useRef, useState } from 'react';

import PriorityLinks, {
  CreateJobListingMenuLink,
  CreateListingMenuLink,
  CreatePerksPostMenuLink,
  EditListingMenuLink,
} from './PriorityLinks';
import LinksMenu from './LinksMenu';

import css from './CustomLinksMenu.module.css';
import { UserType } from '../../../../../util/enums';

const draftId = '00000000-0000-0000-0000-000000000000';
const createListingLinkConfig = intl => ({
  group: 'primary',
  text: intl.formatMessage({ id: 'TopbarDesktop.createListing' }),
  type: 'internal',
  route: {
    name: 'EditListingPage',
    params: { slug: 'draft', id: draftId, type: 'new', tab: 'details' },
  },
  highlight: true,
});

/**
 * Group links to 2 groups:
 * - priorityLinks (Those primary links that fit into current width of the TopbarDesktop.)
 * - menuLinks (The rest of the links that are shown inside dropdown menu.)
 *
 * @param {*} links array of link configs in an order where primary group is shown first
 * @param {*} containerWidth width reserved for the CustomLinksMenu component
 * @param {*} menuMoreWidth width that the "More" label takes
 * @returns Object containing arrays: { priorityLinks, menuLinks }
 */
const groupMeasuredLinks = (links, containerWidth, menuMoreWidth) => {
  const isMeasured = !!links?.[0]?.width && menuMoreWidth > 0;
  const hasNoPrimaryLinks = !links.find(l => l.group === 'primary');
  // - We can't calculate groups, if the width of the rendered links are not measured
  // - We don't need to calculate groups, if links don't contain any 'primary' links
  if (!isMeasured || hasNoPrimaryLinks) {
    return { priorityLinks: [], menuLinks: links };
  }

  const groupedLinks = links.reduce(
    (pickedLinks, link, i) => {
      const isPrimary = link.group === 'primary';
      const isLast = i === links.length - 1;
      // Has menuLinks at this point of the iteration (seconary links are at the end of the array)
      const hasMenuLinks = pickedLinks.menuLinks?.length > 0;

      const hasSpace =
        isLast && !hasMenuLinks
          ? link.cumulatedWidth <= containerWidth
          : link.cumulatedWidth + menuMoreWidth <= containerWidth;

      return isPrimary && hasSpace
        ? {
            priorityLinks: [...pickedLinks.priorityLinks, link],
            menuLinks: pickedLinks.menuLinks,
          }
        : {
            priorityLinks: pickedLinks.priorityLinks,
            menuLinks: [...pickedLinks.menuLinks, link],
          };
    },
    { priorityLinks: [], menuLinks: [] }
  );
  return groupedLinks;
};

const calculateContainerWidth = (containerRefTarget, parentWidth) => {
  if (!containerRefTarget || !containerRefTarget.parentNode) {
    return 0; // Default width if container or parent is not ready
  }

  const siblingArray = Array.from(containerRefTarget.parentNode.childNodes).filter(
    n => n !== containerRefTarget
  );
  const siblingWidthsCombined = siblingArray.reduce((acc, node) => acc + node.offsetWidth, 0);

  const parentStyleMap = containerRefTarget.parentElement?.computedStyleMap
    ? containerRefTarget.parentElement.computedStyleMap()
    : null;
  const topbarPaddingRight = parentStyleMap?.get('padding-right')?.value || 24;

  return parentWidth - siblingWidthsCombined - topbarPaddingRight;
};

/**
 * Create CustomLinksMenu component that takes all the extra space from the TopbarDesktop and
 * renders primary links directly there if there's enough space - and secondary links are shown inside a "More" menu.
 * If the space is limited, primary links as well as secondary links are shown inside the "More" menu.
 *
 * Note: this component is inherently a bit fragile as it needs to deal with DOM directly. If you customize TopbarDesktop,
 * test the responsiveness thoroughly.
 *
 * props:
 * - customLinks: array of link configurations.
 * - hasClientSideContentReady: indicates if TopbarDesktop is ready to render
 * - currentPage: string that indicates if this is "LandingPage" or "SearchPage", etc.
 * - intl: React Intl instance
 *
 * @param {*} props contains currentPage, customLinks, intl, and hasClientSideContentReady
 * @returns component to be placed inside TopbarDesktop
 */
const CustomLinksMenu = ({
  currentPage,
  customLinks = [],
  hasClientSideContentReady,
  intl,
  currentUserListing,
  isTopbarMobileMenuLink,
  userType,
}) => {
  const containerRef = useRef(null);
  const observer = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [moreLabelWidth, setMoreLabelWidth] = useState(0);
  const [links, setLinks] = useState([...customLinks]);
  const [layoutData, setLayoutData] = useState({
    priorityLinks: links,
    menuLinks: links,
    containerWidth: 0,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let animationFrameId = null;

    if (hasClientSideContentReady && moreLabelWidth > 0) {
      // ResizeObserver sets layout data: grouped priority links and links that go to menuLinks dropdown
      observer.current = new ResizeObserver(entries => {
        const containerRefParentWidth = containerRef.current?.parentNode?.offsetWidth;
        const bodyOffsetWidth = document.body.offsetWidth;

        for (const entry of entries) {
          // Body's width has changed (aka viewport has changed)
          const isBodyTheTarget = entry.target === document.body;
          // If the width of the TopbarDesktop (aka <nav>) changes, this assumes that the document.body has the correct width.
          const hasWidthOfTopbarDesktopChanged =
            !isBodyTheTarget && containerRefParentWidth !== bodyOffsetWidth;

          if (isBodyTheTarget || hasWidthOfTopbarDesktopChanged) {
            const target = containerRef?.current;
            if (!target) continue;

            const availableContainerWidth = calculateContainerWidth(target, bodyOffsetWidth);

            // The groupedLinks variable contains { priorityLinks, menuLinks }
            const groupedLinks = groupMeasuredLinks(links, availableContainerWidth, moreLabelWidth);
            // The setLayoutData call affects the UI. This pushes the painting to the next frame.
            animationFrameId = window.requestAnimationFrame(() => {
              setLayoutData({ ...groupedLinks, containerWidth: availableContainerWidth });
            });
            // After setLayoutData is called, don't process other entries
            break;
          }
        }
      });

      if (containerRef?.current) {
        // Observe document.body and the component's own container
        observer.current.observe(document.body);
        observer.current.observe(containerRef.current);
      }
    }

    return () => {
      if (observer.current) {
        try {
          if (document.body) observer.current.unobserve(document.body);
          if (containerRef?.current) observer.current.unobserve(containerRef.current);
        } catch (err) {
          console.error('Failed to unobserve elements:', err);
        }
      }
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [containerRef, hasClientSideContentReady, moreLabelWidth, links]);

  const { priorityLinks, menuLinks, containerWidth } = layoutData;

  // If there are no custom links, just render createListing link.
  // if (customLinks?.length === 0) {
  //   return <CreateListingMenuLink customLinksMenuClass={css.createListingLinkOnly} />;
  // }
  if ([UserType.SCHOOL, UserType.BRAND].includes(userType) && currentUserListing?.id) {
    return (
      <>
        {/* <CreateJobListingMenuLink />
        <CreatePerksPostMenuLink /> */}
        <EditListingMenuLink
          customLinksMenuClass={css.createListingLinkOnly}
          currentUserListing={currentUserListing}
          isTopbarMobileMenuLink={isTopbarMobileMenuLink}
        />
      </>
    );
  }
  if (currentUserListing?.id) {
    return (
      <EditListingMenuLink
        customLinksMenuClass={css.createListingLinkOnly}
        currentUserListing={currentUserListing}
        isTopbarMobileMenuLink={isTopbarMobileMenuLink}
      />
    );
  }
  const styleMaybe = mounted ? { style: { width: `${containerWidth}px` } } : {};
  const isMeasured = !!links?.[0]?.width;
  const hasMenuLinks = menuLinks?.length > 0;
  const hasPriorityLinks = isMeasured && priorityLinks.length > 0;

  return (
    <div className={css.customLinksMenu} ref={containerRef}>
      <PriorityLinks
        links={links}
        priorityLinks={priorityLinks}
        setLinks={setLinks}
        currentUserListing={currentUserListing}
        isTopbarMobileMenuLink={isTopbarMobileMenuLink}
      />

      {hasMenuLinks ? (
        <LinksMenu
          id="linksMenu"
          currentPage={currentPage}
          links={menuLinks}
          showMoreLabel={hasPriorityLinks}
          moreLabelWidth={moreLabelWidth}
          setMoreLabelWidth={setMoreLabelWidth}
          intl={intl}
        />
      ) : null}
    </div>
  );
};

export default CustomLinksMenu;
